import { useEffect, useMemo, useState } from "react";
import { http } from "../lib/api";
import { CopyBtn } from "../components/CopyBtn";
import { Cpu, ExternalLink, Shuffle } from "lucide-react";
import { track } from "../lib/analytics";

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

export const Models = () => {
  const [models, setModels] = useState([]);
  const [active, setActive] = useState(null);
  const [exampleIdx, setExampleIdx] = useState(0);

  useEffect(() => {
    http.get("/models").then((r) => {
      setModels(r.data);
      setActive(r.data[0]?.id);
    });
  }, []);

  const sel = models.find((m) => m.id === active);
  const examples = useMemo(() => (sel?.examples && sel.examples.length ? sel.examples : (sel?.example ? [sel.example] : [])), [sel]);

  // When model changes: random-pick a starting example so it feels fresh each visit
  useEffect(() => {
    if (examples.length) setExampleIdx(Math.floor(Math.random() * examples.length));
  }, [sel?.id, examples.length]);

  const shuffle = () => {
    if (examples.length < 2) return;
    let next = exampleIdx;
    while (next === exampleIdx) next = Math.floor(Math.random() * examples.length);
    setExampleIdx(next);
  };

  const currentExample = examples[exampleIdx] || "";

  return (
    <div data-testid="models-page" className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// module.models</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
          one prompt does not fit all
        </h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">
          Each model has its own dialect. Learn the quirks, the tag syntax, the weighting rules, and the tips that
          separate 30%-output from 90%-output.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-0 border border-white/10">
        <div className="border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0a0a0a] max-h-[70vh] overflow-y-auto">
          {models.map((m) => (
            <button
              key={m.id}
              data-testid={`model-tab-${m.id}`}
              onClick={() => setActive(m.id)}
              className={`w-full text-left px-5 py-4 border-b border-white/5 flex items-center gap-3 duration-150 ${
                active === m.id
                  ? "bg-[#161616] text-[color:var(--accent-amber)] border-l-2 border-l-[color:var(--accent-amber)]"
                  : "hover:bg-[#111] text-neutral-300"
              }`}
              style={{ transitionProperty: "background-color, color" }}
            >
              <Cpu className="w-4 h-4 opacity-60" />
              <div>
                <div className="text-sm font-bold">{m.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{m.type}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="p-8 lg:p-12 bg-[#050505] min-h-[500px]">
          {sel && (
            <div key={sel.id} className="reveal">
              <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                {sel.vendor} // {sel.family}
              </div>
              <div className="flex flex-wrap items-baseline justify-between gap-3 mt-2">
                <h2 className="font-display font-bold text-3xl">{sel.name}</h2>
                {sel.url && (
                  <a
                    href={sel.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => track("model_external_link_clicked", { model_id: sel.id, model_name: sel.name, vendor: sel.vendor })}
                    data-testid={`model-launch-${sel.id}`}
                    className="inline-flex items-center gap-2 bg-[color:var(--accent-amber)] text-black px-4 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-white duration-150"
                    style={{ transitionProperty: "background-color" }}
                  >
                    go to {sel.name} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                <Section title="STRENGTHS" items={sel.strengths} color="var(--accent-matrix)" />
                <Section title="QUIRKS" items={sel.quirks} color="var(--accent-flare)" />
                <Section title="BEST FOR" items={sel.best_for} color="var(--accent-amber)" />
                <Section title="PRO TIPS" items={sel.tips} color="#ededed" />
              </div>

              <div className="mt-10">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    EXAMPLE PROMPT {examples.length > 1 && (
                      <span className="text-[color:var(--accent-amber)] ml-2">{exampleIdx + 1}/{examples.length}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {examples.length > 1 && (
                      <button
                        data-testid={`shuffle-example-${sel.id}`}
                        onClick={shuffle}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-widest border border-white/15 hover:border-[color:var(--accent-matrix)] hover:text-[color:var(--accent-matrix)] duration-150"
                        style={{ transitionProperty: "color, border-color" }}
                      >
                        <Shuffle className="w-3 h-3" /> shuffle
                      </button>
                    )}
                    <CopyBtn text={currentExample} testId={`copy-example-${sel.id}`} />
                  </div>
                </div>
                <pre className="bg-black border border-white/10 p-5 text-xs leading-relaxed whitespace-pre-wrap text-neutral-200">
{currentExample}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, items, color }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color }}>{title}</div>
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={`${title}-${it}-${i}`} className="text-sm text-neutral-300 flex gap-2">
          <span className="text-neutral-600">▸</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);
